import axios from "axios";
import { getUserId } from "./user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Endpoints that need the session cookie or Bearer token (auth, usage,
// billing, paywall state). Everything else (currencies, exchange,
// translations, scans, events) is keyed by X-Device-Id and works fine
// without credentials, which avoids the "Access-Control-Allow-Origin: *"
// + credentials CORS conflict in Capacitor iOS WebViews.
const CREDENTIALED_PREFIXES = [
  "/auth/",
  "/usage",
  "/payments/",
  "/iap/",
];

const TOKEN_LS_KEY = "tiplingo_session_token_v1";

function needsCredentials(url = "") {
  // axios `url` is the path relative to baseURL (e.g. "/usage").
  return CREDENTIALED_PREFIXES.some((p) => url.startsWith(p));
}

export function setStoredSessionToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_LS_KEY, token);
    else localStorage.removeItem(TOKEN_LS_KEY);
  } catch {
    /* localStorage disabled — non-fatal */
  }
}

export function getStoredSessionToken() {
  try {
    return localStorage.getItem(TOKEN_LS_KEY) || null;
  } catch {
    return null;
  }
}

const client = axios.create({
  baseURL: API,
  // 35s default hard timeout so the UI is NEVER stuck on "Translating…"
  // forever when the backend is slow or the network drops mid-request.
  // The backend's /api/translate caps itself at 28s (PER_ATTEMPT_TIMEOUT
  // + OVERALL_BUDGET), so 35s gives Cloudflare a 7s buffer to relay the
  // response. If the response doesn't arrive in that window, axios
  // fires ECONNABORTED → TranslatePanel shows "Translation service is
  // busy" → user can retry. Specific calls can override via per-request
  // config (e.g. file uploads).
  timeout: 35000,
});

// T46 — One-shot launch diagnostic. Build 67 surfaced
// "Request failed with status code 404" on Apple sign-in, but the
// preview backend serves /api/auth/apple correctly. The most likely
// cause is a stale `frontend/build/` from before the route was added,
// or a different REACT_APP_BACKEND_URL was bundled into the iOS
// archive. This NSLog-visible line lets the user (or us, via
// Console.app during a TestFlight session) confirm exactly which
// backend the running build was compiled against. Cheap insurance —
// log fires once at module load.
if (typeof console !== "undefined") {
  console.info("[T46] api baseURL =", API);
}

client.interceptors.request.use((config) => {
  const wantsCreds = needsCredentials(config.url || "");

  // iOS Capacitor — never send cookies. The preview URL is fronted by
  // Cloudflare which force-adds `Access-Control-Allow-Origin: *`, and that
  // combined with `Access-Control-Allow-Credentials: true` is a CORS
  // combination WKWebView rejects pre-flight (axios throws "Network Error").
  // We exclusively use the Bearer token fallback on iOS — cookies aren't
  // reliable across the `capacitor://localhost` origin anyway.
  const onIOS =
    typeof window !== "undefined" &&
    typeof window.Capacitor !== "undefined" &&
    window.Capacitor?.isNativePlatform?.() &&
    window.Capacitor?.getPlatform?.() === "ios";

  config.withCredentials = wantsCreds && !onIOS;

  const PUBLIC_PREFIXES = ["/currencies", "/exchange", "/events"];
  const isPublic = PUBLIC_PREFIXES.some((p) =>
    (config.url || "").startsWith(p)
  );
  const token = getStoredSessionToken();
  if (token && !isPublic) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    config.headers["X-Device-Id"] = getUserId();
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Could not attach X-Device-Id:", err?.message || err);
    }
  }
  return config;
});

// Auto-capture the session_token returned by /auth/session so subsequent
// requests can use the Bearer fallback transparently.
client.interceptors.response.use(
  (response) => {
    try {
      if (
        response?.config?.url?.includes("/auth/session") &&
        response.data?.session_token
      ) {
        setStoredSessionToken(response.data.session_token);
      }
    } catch {
      /* non-fatal */
    }
    return response;
  },
  async (error) => {
    // Auto-retry transient gateway errors (502/503/504) ONCE with a
    // small backoff. Cloudflare occasionally returns 502 when the
    // backend is briefly busy (LLM retry storm, container cold-start);
    // a single retry usually recovers transparently so the user never
    // sees a "Translation failed" toast for a hiccup.
    //
    // Only retries idempotent paths AND only POSTs we whitelist below
    // (translate, tts, voice-pref). We DELIBERATELY do not retry every
    // POST because /tips, /splits, /events etc. are not safe to fire
    // twice.
    try {
      const cfg = error?.config;
      const status = error?.response?.status;
      const RETRY_STATUSES = new Set([502, 503, 504]);
      const RETRYABLE_PATHS = [
        "/translate",
        "/tts",
        "/auth/voice-pref",
        "/auth/me",
        "/usage",
        "/currencies",
        "/exchange",
      ];
      const isRetryable =
        cfg &&
        RETRY_STATUSES.has(status) &&
        RETRYABLE_PATHS.some((p) => (cfg.url || "").startsWith(p)) &&
        !cfg.__retried;
      if (isRetryable) {
        cfg.__retried = true;
        await new Promise((r) => setTimeout(r, 1200));
        return client.request(cfg);
      }
    } catch {
      /* non-fatal — fall through to reject below */
    }
    return Promise.reject(error);
  },
);

export default client;
export { API };
